import {Container} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import type {TextBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the props used by the `TextBlock` component.
 * It includes essential metadata and content configuration necessary to render a timeline section with animated entries.
 * Each property reflects a specific feature of the timeline, including display content, structure, and test instrumentation.
 */
interface ComponentProps {
    /**
     * The unique identifier for the timeline block.
     * This is used for DOM identification and reference within the page layout.
     */
    id: TextBlockType['id'];
    /**
     * A string representing an optional link anchor ID for in-page navigation.
     * If defined, it allows other elements or menus to jump directly to this block.
     */
    linkId: TextBlockType['linkId'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'TextBlock'
     */
    testId?: string;
    /**
     * An array of timeline events to be rendered in chronological order.
     * Each event contains information like year, title, description, and image.
     */
    text: TextBlockType['text'];
}

/**
 * The `TextBlock` component is a visual block used to render a chronological list of events with animations.
 * It wraps a sequence of timeline entries using the `TimelineItem` component and decorates them with a vertical line
 * that animates based on scroll position to indicate progression through the timeline.
 * Internally, this component uses `useTextBlock` to handle references, animated progress state, and positioning of the line.
 * The component also ensures that the first and last items are tracked for calculating the scrollable animation range,
 * and applies alternating styling (even/odd) to distinguish items visually. It is reactive thanks to `mobx-react-lite`'s `observer`.
 *
 * @param props          The props object for TextBlock.
 * @param props.events   An array of event objects that each contain details like year, title, description, and image.
 * @param props.id       The unique ID for the block used for DOM identification and accessibility.
 * @param props.linkId   The ID used as an anchor target for in-page navigation.
 * @param props.overline A decorative text displayed above the title.
 * @param props.testId   A test identifier used for automated UI testing.
 * @param props.title    The main title of the timeline block section.
 * @returns A rendered section element containing timeline items and an animated progress line.
 *
 * @example
 * ```tsx
 * <TextBlock
 *   id="company-history"
 *   title="Our Journey"
 *   overline="Milestones"
 *   events={[
 *     { year: 2010, title: 'Founded', description: '...', image: ... },
 *     { year: 2015, title: 'Expansion', description: '...', image: ... }
 *   ]}
 * />
 * ```
 */
const TextBlock = observer(({testId = 'TextBlock', text}: ComponentProps) => (
    <ViewportTrigger baseComponent={Container} testId={testId}>
        <motion.div variants={FadeInAnimation}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {text && <PayloadLexical description={text} />}
        </motion.div>
    </ViewportTrigger>
));

TextBlock.displayName = 'TextBlock';

export {TextBlock};